import { SheetRenderer } from "@/components/r3f/renderers/sheet-renderer";
import { useViewIfNeeded } from "@/hooks/use-view-if-needed";
import { SheetObject } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { PickingToolsCallbacks } from "@/tools/use-picking-tools-callbacks";
import { selectAncestor, useOnClick } from "@faro-lotv/app-component-toolbox";
import {
  IElement,
  IElementType,
  IElementTypeHint,
  isIElementWithTypeAndHint,
} from "@faro-lotv/ielement-types";
import { FloorPlanBackgroundTransparency, LodFloorPlan } from "@faro-lotv/lotv";
import { ThreeEvent } from "@react-three/fiber";
import { JSX, useCallback, useMemo } from "react";
import { Mesh } from "three";
import { SceneContextMenuData } from "./scene-context-menu/use-scene-context-menu";

type SheetsRendererProps = {
  /** The current active sheets */
  sheets: Array<SheetObject | undefined>;
  /** Flag to make the sheet transparent */
  transparentSheet?: boolean;
  /** Picking tool callback handlers to manage sheet interactions*/
  pickingToolsCallbacks?: PickingToolsCallbacks;
  /** Callback issued when a sheet is clicked */
  clickOnSheet(ev: ThreeEvent<MouseEvent>): void;
  /** Callback when a context menu is requested on a sheet */
  onSheetContextMenu?(contextMenuData: SceneContextMenuData): void;
  /** Flag to enable sheet views @default false */
  enableSheetViews?: boolean;
};

/**
 * @returns a component to render a collection of sheet objects
 */
export function SheetsRenderer({
  sheets,
  transparentSheet,
  pickingToolsCallbacks,
  clickOnSheet,
  onSheetContextMenu,
  enableSheetViews = false,
}: SheetsRendererProps): JSX.Element {
  const activeTool = useAppSelector(selectActiveTool);

  const { onPointerDown, onClick } = useOnClick(clickOnSheet, true);

  return (
    <>
      {sheets.map(
        (sheet, index) =>
          sheet && (
            <SingleSheetRenderer
              sheet={sheet}
              transparentSheet={transparentSheet}
              activeTool={activeTool}
              pickingToolsCallbacks={pickingToolsCallbacks}
              onPointerDown={onPointerDown}
              onClick={onClick}
              onSheetContextMenu={onSheetContextMenu}
              key={sheet.iElement.id}
              index={index}
              enableSheetView={enableSheetViews}
            />
          ),
      )}
    </>
  );
}

type SingleSheetRendererProps = {
  /** The sheet object to render */
  sheet: SheetObject;
  /** Whether sheets should be transparent */
  transparentSheet?: boolean;
  /** The currently active tool */
  activeTool: ToolName | null;
  /** Sets of callback to access the active picking tool */
  pickingToolsCallbacks?: PickingToolsCallbacks;
  /** Callback to be issued when a pointer is pressed on the sheet */
  onPointerDown(ev: ThreeEvent<PointerEvent>): void;
  /** Callback to be issued when the sheet is clicked */
  onClick(ev: ThreeEvent<MouseEvent>): void;
  /** Callback to be issued when the context menu is invoked on the sheet */
  onSheetContextMenu?(contextMenuData: SceneContextMenuData): void;
  /** Progressive numerical index in the sheets to be rendered */
  index: number;
  /** Flag to enable sheet views */
  enableSheetView?: boolean;
};

function SingleSheetRenderer({
  sheet,
  transparentSheet,
  activeTool,
  pickingToolsCallbacks,
  onPointerDown,
  onClick,
  onSheetContextMenu,
  index,
  enableSheetView = false,
}: SingleSheetRendererProps): JSX.Element {
  const isPhotogrammetryOverviewMap = !!useAppSelector(
    selectAncestor(sheet.iElement, isIElementSectionPhotogrammetryScan),
  );

  const backgroundTransparent = isPhotogrammetryOverviewMap
    ? FloorPlanBackgroundTransparency.No
    : FloorPlanBackgroundTransparency.Alpha;

  const onPointerMove = useCallback(
    (ev: ThreeEvent<MouseEvent>) =>
      pickingToolsCallbacks?.onModelHovered(ev, sheet.iElement.id),
    [pickingToolsCallbacks, sheet.iElement.id],
  );

  const onSheetClicked = useCallback(
    (ev: ThreeEvent<MouseEvent>) =>
      pickingToolsCallbacks?.onModelClicked(ev, sheet.iElement.id),
    [pickingToolsCallbacks, sheet.iElement.id],
  );

  const onContextMenu = useCallback(
    (ev: ThreeEvent<MouseEvent>) => {
      if (activeTool) return;

      onSheetContextMenu?.({
        position: ev.point,
        target: sheet.iElement,
      });
    },
    [activeTool, onSheetContextMenu, sheet.iElement],
  );

  const onWheel = useCallback(
    (ev: ThreeEvent<WheelEvent>) =>
      pickingToolsCallbacks?.onModelZoomed(ev, sheet.iElement.id),
    [pickingToolsCallbacks, sheet.iElement.id],
  );

  const view = useSheetViewIfNeeded(sheet, enableSheetView);

  return (
    <SheetRenderer
      sheet={view}
      transparent={transparentSheet}
      backgroundTransparent={backgroundTransparent}
      dark={isPhotogrammetryOverviewMap ? false : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={activeTool ? onPointerMove : undefined}
      onClick={activeTool && pickingToolsCallbacks ? onSheetClicked : onClick}
      onContextMenu={onContextMenu}
      onWheel={activeTool ? onWheel : undefined}
      index={index}
    />
  );
}

function isIElementSectionPhotogrammetryScan(iElement: IElement): boolean {
  return isIElementWithTypeAndHint(
    iElement,
    IElementType.section,
    IElementTypeHint.photogrammetryScan,
  );
}

function useSheetViewIfNeeded(
  sheet: SheetObject,
  enableSheetView: boolean,
): SheetObject {
  const lodView = useViewIfNeeded(
    sheet instanceof LodFloorPlan ? sheet : null,
    enableSheetView,
  );

  const meshClone = useMemo(() => {
    if (enableSheetView && sheet instanceof Mesh) {
      const clone = new Mesh(sheet.geometry.clone(), sheet.material.clone());
      return Object.assign(clone, { iElement: sheet.iElement });
    }
    return sheet;
  }, [sheet, enableSheetView]);

  return lodView ?? meshClone;
}
